import { history } from '@umijs/max';
import { message } from 'antd';
import { stripAppBase } from '../../config/appPath';

export const ACCESS_TOKEN_KEY = 'access_token';
const LOGIN_PATH = '/user/login';
const ERROR_MESSAGE_MAP: Record<string, string> = {
  BID_CODE_DUPLICATED: '竞标编号已存在，请修改后重试',
};

export function getAccessToken() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(ACCESS_TOKEN_KEY) || '';
}

export function persistAccessToken(token?: string) {
  if (typeof window === 'undefined') {
    return;
  }

  if (token) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, token);
    return;
  }

  window.localStorage.removeItem(ACCESS_TOKEN_KEY);
}

function normalizeUrl(url: string) {
  if (url.startsWith('/api/')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `/api${url}`;
  }
  return `/api/${url}`;
}

function redirectToLogin() {
  if (typeof window === 'undefined') {
    return;
  }

  if (stripAppBase(window.location.pathname) === LOGIN_PATH) {
    return;
  }

  const redirect = `${window.location.pathname}${window.location.search}`;
  history.replace({
    pathname: LOGIN_PATH,
    search: new URLSearchParams({ redirect }).toString(),
  });
}

function normalizeApiErrorMessage(message?: string) {
  if (!message) {
    return '';
  }
  const matchedCode = Object.keys(ERROR_MESSAGE_MAP).find((code) =>
    message.includes(code),
  );
  return matchedCode ? ERROR_MESSAGE_MAP[matchedCode] : message;
}

async function parseResponse(response: Response) {
  const text = await response.text();
  let data: any = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (!response.ok) {
        throw new Error(getResponseErrorMessage(response));
      }
      throw new Error('服务端返回了非 JSON 响应');
    }
  }

  if (response.status === 401) {
    persistAccessToken();
    redirectToLogin();
  }

  if (!response.ok) {
    throw new Error(
      normalizeApiErrorMessage(
        data?.message ||
          data?.error_description ||
          data?.error ||
          getResponseErrorMessage(response),
      ),
    );
  }

  // Unwrap ApiResponse<T> wrapper if present
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
    if (!data.success) {
      throw new Error(normalizeApiErrorMessage(data?.message) || '请求失败');
    }
    return data.data;
  }

  return data;
}

function getResponseErrorMessage(response: Response) {
  if (response.status === 502) {
    return '服务暂时不可用，请稍后重试';
  }

  if (response.status === 503) {
    return '服务正在维护中，请稍后重试';
  }

  if (response.status === 504) {
    return '服务响应超时，请稍后重试';
  }

  return `请求失败 (${response.status})`;
}

function shouldThrowByDefault(method?: string) {
  return !method || !['GET', 'HEAD'].includes(method.toUpperCase());
}

function reportRequestError(error: unknown) {
  const errorMessage =
    error instanceof Error ? error.message : '请求失败，请稍后重试';
  message.error(errorMessage);
}

export async function apiRequest(
  url: string,
  options: RequestInit & {
    fallbackData?: any;
    silentError?: boolean;
    throwOnError?: boolean;
    withAuth?: boolean;
  } = {},
) {
  const {
    headers,
    withAuth = true,
    credentials = 'include',
    fallbackData = null,
    silentError = false,
    throwOnError,
    ...restOptions
  } = options;

  const nextHeaders = new Headers(headers || {});
  const method = restOptions.method?.toUpperCase() || 'GET';
  const shouldThrow = throwOnError ?? shouldThrowByDefault(method);

  if (withAuth) {
    const token = getAccessToken();
    if (token && !nextHeaders.has('authorization')) {
      nextHeaders.set('authorization', `bearer ${token}`);
    }
  }

  try {
    const response = await fetch(normalizeUrl(url), {
      credentials,
      ...restOptions,
      headers: nextHeaders,
    });

    return await parseResponse(response);
  } catch (error) {
    if (shouldThrow) {
      throw error;
    }

    if (!silentError) {
      reportRequestError(error);
    }

    return fallbackData;
  }
}
